import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { LoginService } from '../login.service';
import { ExamsService } from '../exams.service';
import {
  AnnouncementBubble, AnnouncementService, AnnouncementTrigger, CornerPosition,
} from '../announcement.service';

// Global popup host for UMT-managed "bubbles". Mounted once in app.component.html
// next to <app-version-popup>. Copied as-is into every client (aweb, gastroAweb,
// eyes-aweb, usgaweb, awebAppointments) — nothing here is product-specific,
// since the backend already resolves targeting. The one per-repo difference
// is the "tab changed" hook wired in ngOnInit (this repo's ExamComponent
// pushes into LoginService.tabChanged$ from its setPage() handler — see
// exam.component.ts).
@Component({
  standalone: true,
  imports: [CommonModule],
  selector: 'app-announcement-host',
  templateUrl: './announcement-host.component.html',
  styleUrls: ['./announcement-host.component.scss'],
})
export class AnnouncementHostComponent implements OnInit {
  fullscreenBubble: AnnouncementBubble | null = null;
  cornerBubbles: AnnouncementBubble[] = [];

  constructor(
    private announcementService: AnnouncementService,
    private ls: LoginService,
    private examsService: ExamsService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.ls.onLoginComplete.subscribe(() => this.load('login'));
    this.examsService.examLoaded$.subscribe(() => this.load('exam'));
    this.ls.tabChanged$.subscribe((tabKey) => this.load('tab', tabKey));
  }

  private load(trigger: AnnouncementTrigger, tabKey?: string) {
    this.announcementService.fetch(trigger, tabKey).subscribe({
      next: (bubbles) => this.show(bubbles),
      error: () => {},
    });
  }

  // At most one fullscreen bubble (the backend already sorts by priority) plus
  // up to two corner bubbles, per the product rules. Every bubble surfaced
  // here is a genuinely new view (a fresh login, exam-entry or tab-switch
  // trigger), so it always records — a component-lifetime dedup Set would
  // survive across logout/login (an in-app navigation, not a page reload)
  // and undercount, which is exactly what broke the MaxViews cap.
  private show(bubbles: AnnouncementBubble[]) {
    this.fullscreenBubble = bubbles.find((b) => b.displayMode === 'fullscreen') || null;
    this.cornerBubbles = bubbles.filter((b) => b.displayMode === 'corner').slice(0, 2);

    const shown = this.fullscreenBubble ? [this.fullscreenBubble, ...this.cornerBubbles] : this.cornerBubbles;
    for (const b of shown) {
      this.announcementService.recordView(b.id).subscribe({ error: () => {} });
    }
  }

  // Corner bubbles can each pick their own corner — group by position so the
  // template can give each corner its own fixed stack.
  bubblesAt(position: CornerPosition): AnnouncementBubble[] {
    return this.cornerBubbles.filter((b) => b.cornerPosition === position);
  }

  isHebrew(): boolean {
    return (this.translate.currentLang || '').startsWith('he');
  }

  title(b: AnnouncementBubble): string {
    return this.isHebrew() ? (b.titleHe || b.titleEn || '') : (b.titleEn || b.titleHe || '');
  }

  body(b: AnnouncementBubble): string {
    return this.isHebrew() ? (b.bodyHe || b.bodyEn || '') : (b.bodyEn || b.bodyHe || '');
  }

  // Direction is an explicit editorial choice made per box in UMT (either box
  // can hold either language) — follow whichever box body() actually picked.
  // The title shares the same box's direction (there's no separate title
  // direction setting — one choice per language box, covers title + body).
  bodyDir(b: AnnouncementBubble): 'rtl' | 'ltr' {
    const usingHe = this.isHebrew() ? !!b.bodyHe : !b.bodyEn && !!b.bodyHe;
    return usingHe ? (b.bodyHeDir || 'rtl') : (b.bodyEnDir || 'ltr');
  }

  titleDir(b: AnnouncementBubble): 'rtl' | 'ltr' {
    const usingHe = this.isHebrew() ? !!b.titleHe : !b.titleEn && !!b.titleHe;
    return usingHe ? (b.bodyHeDir || 'rtl') : (b.bodyEnDir || 'ltr');
  }

  ctaText(b: AnnouncementBubble): string {
    return this.isHebrew() ? (b.ctaTextHe || b.ctaTextEn || '') : (b.ctaTextEn || b.ctaTextHe || '');
  }

  // Authored per bubble in UMT, not routed through the app's translation files.
  dontShowAgainText(b: AnnouncementBubble): string {
    return this.isHebrew() ? (b.dontShowAgainTextHe || b.dontShowAgainTextEn) : (b.dontShowAgainTextEn || b.dontShowAgainTextHe);
  }

  // null values are fine here — [ngStyle] simply leaves those properties at
  // their CSS-default (the app's normal look) when a bubble has no override.
  colorStyle(b: AnnouncementBubble): Record<string, string | null> {
    return { 'background-color': b.backgroundColor, color: b.textColor };
  }

  // For the title element specifically — only the text color, so a custom
  // backgroundColor on the card doesn't also paint a background behind the title.
  textColorStyle(b: AnnouncementBubble): Record<string, string | null> {
    return { color: b.textColor };
  }

  cornerCardStyle(b: AnnouncementBubble): Record<string, string | null> {
    const style: Record<string, string | null> = {
      ...this.colorStyle(b),
      width: `${b.cornerWidthPercent}vw`,
    };
    if (b.cornerHeightPercent) {
      style['height'] = `${b.cornerHeightPercent}vh`;
      style['overflow-y'] = 'auto';
    }
    return style;
  }

  onCta(b: AnnouncementBubble) {
    this.announcementService.dismiss(b.id, 'cta').subscribe({ error: () => {} });
    if (b.ctaUrl) window.open(b.ctaUrl, '_blank');
    this.remove(b);
  }

  // Without closeDismissesForever, closing only hides it for this visit —
  // nothing is written server-side, so it comes back on the next trigger.
  onClose(b: AnnouncementBubble) {
    if (b.closeDismissesForever) {
      this.announcementService.dismiss(b.id, 'close').subscribe({ error: () => {} });
    }
    this.remove(b);
  }

  onDontShowAgain(b: AnnouncementBubble) {
    this.announcementService.dismiss(b.id, 'dontShowAgain').subscribe({ error: () => {} });
    this.remove(b);
  }

  private remove(b: AnnouncementBubble) {
    if (this.fullscreenBubble?.id === b.id) this.fullscreenBubble = null;
    this.cornerBubbles = this.cornerBubbles.filter((x) => x.id !== b.id);
  }
}
